import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Mail, MailOpen, Trash2, Eye } from "lucide-react";

export default function AdminMessages() {
  const [messages, setMessages] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setMessages(data);
    } catch {
      setMessages([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      const { data } = await api.put(`/admin/messages/${id}/read`);
      setMessages((ms) => ms.map((m) => (m.id === id ? data : m)));
    } catch { /* non-blocking */ }
  };

  const openMessage = (m) => {
    setSelected(m);
    if (!m.read) markRead(m.id);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((ms) => ms.filter((m) => m.id !== id));
      toast.success("Message deleted");
      if (selected?.id === id) setSelected(null);
    } catch {
      toast.error("Failed to delete message");
    }
  };

  const unread = (messages || []).filter((m) => !m.read).length;

  return (
    <div data-testid="admin-messages-page">
      <h1 className="font-serif text-3xl lg:text-4xl text-obsidian mb-1">Messages</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {messages?.length || 0} total{unread > 0 && <> · <span className="text-gold-dark font-semibold">{unread} unread</span></>}
      </p>

      {messages === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gold" /></div>
      ) : messages.length === 0 ? (
        <div className="bg-white border border-black/10 p-12 text-center" data-testid="no-messages">
          <Mail className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-serif text-2xl text-obsidian mb-2">No messages yet</p>
          <p className="text-sm text-muted-foreground">Messages sent from the Contact Us page will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4 font-semibold w-10"></th>
                <th className="p-4 font-semibold">From</th>
                <th className="p-4 font-semibold hidden md:table-cell">Message</th>
                <th className="p-4 font-semibold hidden sm:table-cell">Date</th>
                <th className="p-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id} className={`border-b border-black/5 last:border-0 hover:bg-secondary/40 ${!m.read ? "bg-gold/5" : ""}`} data-testid={`admin-message-${m.id}`}>
                  <td className="p-4">
                    {m.read
                      ? <MailOpen className="w-4 h-4 text-muted-foreground" />
                      : <Mail className="w-4 h-4 text-gold-dark" />}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-obsidian ${!m.read ? "font-semibold" : ""}`}>{m.name}</span>
                      {!m.read && <Badge className="bg-gold text-obsidian border-0 rounded-sm text-[10px] px-1.5 py-0">New</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground break-all">{m.email}</p>
                  </td>
                  <td className="p-4 hidden md:table-cell text-muted-foreground max-w-md truncate">{m.message}</td>
                  <td className="p-4 hidden sm:table-cell text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openMessage(m)} className="p-2 hover:bg-secondary rounded-sm transition-colors" data-testid={`view-message-${m.id}`} title="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => remove(m.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-sm transition-colors" data-testid={`delete-message-${m.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && (
          <DialogContent className="max-w-lg" data-testid="message-detail">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Message from {selected.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="overline text-gold-dark mb-1">Email</p>
                  <a href={`mailto:${selected.email}`} className="text-obsidian underline break-all">{selected.email}</a>
                </div>
                <div>
                  <p className="overline text-gold-dark mb-1">Received</p>
                  <p className="text-muted-foreground">{new Date(selected.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
              <div>
                <p className="overline text-gold-dark mb-2">Message</p>
                <p className="text-sm text-obsidian whitespace-pre-wrap bg-secondary/50 border border-black/10 rounded-sm p-4">{selected.message}</p>
              </div>
              <div className="flex gap-2 pt-2">
                <a href={`mailto:${selected.email}?subject=Re: Your enquiry to ChainSmart`} className="flex-1 inline-flex items-center justify-center gap-2 bg-obsidian text-white font-semibold py-2.5 hover:bg-gold hover:text-obsidian transition-colors rounded-sm text-sm">
                  <Mail className="w-4 h-4" /> Reply by Email
                </a>
                <button onClick={() => remove(selected.id)} className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-500 font-semibold px-4 py-2.5 hover:bg-red-50 transition-colors rounded-sm text-sm">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
